/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-return */
import React, { useEffect, useRef, useState } from 'react'

import CircularProgress from '../CircularProgress'
import ErrorBoundary from '../ErrorBoundary'

import { env } from '@busie/utils'
import palette from '../theme/palette'
import { getMapBounds, getMapPadding, getMarkerIcons } from './helpers'
import { loadHereMaps } from './HereMapsLoader'
import { MapMarkerCoordinates, Props } from './map.types'
import MapStyled from './styled/MapStyled'

const Map: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
  // TODO: [BUSIE-1727] Consolidate Map Components
  const mapRef = useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const map = useRef<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const platform = useRef<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const API_KEY = env.VITE_HERE_MAPS_API_KEY
  useEffect(
    () => {
      // Check if the map object has already been created
      if (!map.current && API_KEY) {
        // Load HERE Maps dynamically
        loadHereMaps()
          .then((H) => {
            // To avoid re-rendering/duplicate map instances, clear the ref.
            if (mapRef.current) mapRef.current.innerHTML = ''
            // Create a platform object with the API key
            platform.current = new H.service.Platform({ apikey: API_KEY })
            // Create a new Raster Tile service instance
            const rasterTileService = platform.current.getRasterTileService({
              queryParams: {
                style: 'explore.day',
                size: 512,
              },
            })
            // Creates a new instance of the H.service.rasterTile.Provider class
            // The class provides raster tiles for a given tile layer ID and pixel format
            const rasterTileProvider = new H.service.rasterTile.Provider(rasterTileService)
            // Create a new Tile layer with the Raster Tile provider
            const rasterTileLayer = new H.map.layer.TileLayer(rasterTileProvider)
            // Create a new map instance with the Tile layer, center and zoom level
            const newMap = new H.Map(mapRef.current, rasterTileLayer, {
              pixelRatio: window.devicePixelRatio,
              center: props.center || { lat: 41.927, lng: -73.9974 },
              zoom: props.zoom || 14,
              padding: getMapPadding(props.isFullPage),
            })

            if (props.route) {
              const linestring = H.geo.LineString.fromLatLngArray(props.route.shapes)

              const routeline = new H.map.Polyline(linestring, {
                data: {},
                style: { strokeColor: palette.red.main, lineWidth: 2 },
              })
              newMap.addObjects([routeline])
            }

            const markerCoordinates: MapMarkerCoordinates = {
              lat: [],
              lng: [],
            }
            const markers: H.map.Object[] = []

            const markerIcons = getMarkerIcons(H)

            //  create markers and routes from coordiantes
            props.markers.forEach((point, index, arr) => {
              // select icon for marker
              let icon = markerIcons.trip(index + 1)
              if (index === 0) icon = markerIcons.start()
              if (index === arr.length - 1) icon = markerIcons.end()

              const lat = point.lat
              const lng = point.lng

              // collect coordinates to render bounds properly
              markerCoordinates.lat.push(lat)
              markerCoordinates.lng.push(lng)

              // collect markers
              const marker = new H.map.Marker({ lat, lng }, { data: [], icon: icon })
              markers.push(marker)
            })
            // render markers
            newMap.addObjects(markers)

            if (markerCoordinates.lat.length && markerCoordinates.lng.length) {
              const rectCoordinates = getMapBounds(markerCoordinates)
              const mapBounds = new H.geo.Rect(
                rectCoordinates.top,
                rectCoordinates.left,
                rectCoordinates.bottom,
                rectCoordinates.right
              )
              newMap.getViewModel().setLookAtData({
                bounds: mapBounds,
              })
            }

            // Add panning and zooming behavior to the map
            new H.mapevents.Behavior(new H.mapevents.MapEvents(newMap))

            // Set the map object to the reference
            map.current = newMap
            setIsLoading(false)
          })
          .catch((error) => {
            console.error('Failed to load HERE Maps:', error)
            setLoadError(error.message || 'Failed to load HERE Maps')
            setIsLoading(false)
          })
      }
    },
    // Dependencies array
    [API_KEY, props]
  )

  if (!API_KEY) {
    return (
      <ErrorBoundary errorText="The Map is not properly configured">
        <div></div>
      </ErrorBoundary>
    )
  }

  if (loadError) {
    return (
      <ErrorBoundary errorText={loadError}>
        <div></div>
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary errorText="Something went wrong with map rendering">
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <MapStyled isFullPage={props.isFullPage} ref={mapRef} />
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
}

export default Map
